import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * SEO — client-side metadata injector for a React SPA.
 *
 * Renders no DOM but imperatively updates <head> for the current route:
 *  - document.title
 *  - <meta name="description">
 *  - <meta name="robots">
 *  - <link rel="canonical">
 *  - Open Graph (og:title, og:description, og:url, og:type, og:image)
 *  - Twitter card equivalents
 *  - Optional JSON-LD structured data (single application/ld+json script)
 *
 * Canonical URL is always built from `SITE_ORIGIN` (never window.location) and
 * strips query strings — this keeps utm_* / tracking params from ending up in
 * the canonical or from creating duplicate index candidates.
 *
 * Googlebot fully executes JS and picks up these tags. We also add non-critical
 * OG/Twitter/JSON-LD so social preview + rich results work once crawled.
 */

const SITE_ORIGIN = "https://spiruva.com";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-default.png`;

function upsertMeta(attr, name, content) {
  if (content === undefined || content === null) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  if (!data) return;
  let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SEO({
  title,
  description,
  path,               // optional override; defaults to current pathname
  robots = "index,follow",
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd = null,      // object or array of objects for structured data
}) {
  const location = useLocation();

  useEffect(() => {
    const pathname = path || location.pathname || "/";
    // strip trailing slash except root, drop query/hash for canonical
    const clean = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
    const canonical = `${SITE_ORIGIN}${clean}`;

    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:site_name", "SPIRUVA");
    if (ogImage) upsertMeta("property", "og:image", ogImage);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    if (ogImage) upsertMeta("name", "twitter:image", ogImage);

    if (jsonLd) {
      const payload = Array.isArray(jsonLd)
        ? { "@context": "https://schema.org", "@graph": jsonLd }
        : { "@context": "https://schema.org", ...jsonLd };
      upsertJsonLd("route", payload);
    }
  }, [title, description, path, location.pathname, robots, ogType, ogImage, jsonLd]);

  return null;
}

export { SITE_ORIGIN };
